import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"

import { axiosInstance } from "@/lib/api-client"
import { API_ROUTES } from "@/lib/api-routes"

import type {
  CancelOptions,
  CancelWhen,
  CarrierGroupCreate,
  CarrierGroupUpdate,
  DialerIPsUpdate,
  LaneCreate,
  LaneRead,
  LaneSummary,
  LanesOverview,
  ReissueQuote,
} from "../types"

export const LANES_KEY = ["lanes"] as const

/**
 * Every lane mutation returns the full updated lane — prime the detail
 * cache from the response and refresh the list. Shared `onSuccess`.
 */
function cacheLane(qc: ReturnType<typeof useQueryClient>) {
  return (updated: LaneRead) => {
    qc.setQueryData(laneQueryOptions(updated.id).queryKey, updated)
    qc.invalidateQueries({ queryKey: LANES_KEY })
    // Orders charge the wallet and raise documents.
    qc.invalidateQueries({ queryKey: ["billing"] })
  }
}

// ── List ────────────────────────────────────────────────────────────
export function lanesQueryOptions() {
  return queryOptions({
    queryKey: [...LANES_KEY, "list"],
    queryFn: async () => {
      const res = await axiosInstance.get<LaneSummary[]>(API_ROUTES.LANES.LIST)
      return res.data
    },
  })
}

export function useLanesQuery() {
  return useQuery(lanesQueryOptions())
}

// ── Page overview (summary + one card per lane) ─────────────────────
export function lanesOverviewQueryOptions() {
  return queryOptions({
    queryKey: [...LANES_KEY, "overview"],
    queryFn: async () => {
      const res = await axiosInstance.get<LanesOverview>(
        API_ROUTES.LANES.OVERVIEW_ALL
      )
      return res.data
    },
    // Server caches this ~15s; keep the snapshot fresh at that cadence
    // (true-live calls/pct come over SSE, overlaid per card).
    refetchInterval: 15_000,
  })
}

export function useLanesOverviewQuery() {
  return useQuery(lanesOverviewQueryOptions())
}

// ── Detail ──────────────────────────────────────────────────────────
export function laneQueryOptions(id: string) {
  return queryOptions({
    queryKey: [...LANES_KEY, id],
    queryFn: async () => {
      const res = await axiosInstance.get<LaneRead>(API_ROUTES.LANES.DETAIL(id))
      return res.data
    },
    // Poll while anything is settling (provisioning, draining, or an IP
    // reissue rebuilding) so the UI catches the new IP / health.
    refetchInterval: (query) => {
      const lane = query.state.data
      if (!lane) return false
      const settling =
        lane.status === "provisioning" ||
        lane.status === "deleting" ||
        (lane.nodes ?? []).some(
          (n) =>
            n.reissue_pending ||
            ["pending", "provisioning", "deleting"].includes(n.status) ||
            ["provisioning", "draining", "deleting"].includes(n.health_status)
        )
      return settling ? 5000 : false
    },
  })
}

export function useLaneQuery(id: string) {
  return useQuery({ ...laneQueryOptions(id), enabled: Boolean(id) })
}

// ── Mutations ───────────────────────────────────────────────────────
/** Rename a lane (`PATCH { name }`); returns the full updated lane. */
export function useRenameLaneMutation(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (name: string) => {
      const res = await axiosInstance.patch<LaneRead>(
        API_ROUTES.LANES.DETAIL(id),
        { name }
      )
      return res.data
    },
    onSuccess: cacheLane(qc),
  })
}

export function useCreateLaneMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: LaneCreate) => {
      const res = await axiosInstance.post<LaneRead>(
        API_ROUTES.LANES.CREATE,
        data
      )
      return res.data
    },
    onSuccess: cacheLane(qc),
  })
}

/**
 * Replace the WHOLE dialer list — send every dialer with its
 * `carrier_group_id`. Returns the full updated lane.
 */
export function useUpdateLaneDialerIpsMutation(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (dialer_ips: DialerIPsUpdate["dialer_ips"]) => {
      const res = await axiosInstance.put<LaneRead>(
        API_ROUTES.LANES.DIALER_IPS(id),
        { dialer_ips }
      )
      return res.data
    },
    onSuccess: cacheLane(qc),
  })
}

/** Add a carrier group to the lane. */
export function useCreateCarrierGroupMutation(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: CarrierGroupCreate) => {
      const res = await axiosInstance.post<LaneRead>(
        API_ROUTES.LANES.CARRIER_GROUPS(id),
        data
      )
      return res.data
    },
    onSuccess: cacheLane(qc),
  })
}

/**
 * Update one carrier group — partial: `{ name }` renames, `{ carriers }`
 * REPLACES that group's carrier list, or both.
 */
export function useUpdateCarrierGroupMutation(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      groupId,
      data,
    }: {
      groupId: string
      data: CarrierGroupUpdate
    }) => {
      const res = await axiosInstance.put<LaneRead>(
        API_ROUTES.LANES.CARRIER_GROUP(id, groupId),
        data
      )
      return res.data
    },
    onSuccess: cacheLane(qc),
  })
}

/** Delete a carrier group (409 if any dialer still points at it). */
export function useDeleteCarrierGroupMutation(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (groupId: string) => {
      const res = await axiosInstance.delete<LaneRead>(
        API_ROUTES.LANES.CARRIER_GROUP(id, groupId)
      )
      return res.data
    },
    onSuccess: cacheLane(qc),
  })
}

/**
 * What cancelling a server (or, with no `nodeId`, the whole lane) allows:
 * whether it can be kept until the end of the paid month, and whether a
 * cancellation is already scheduled. Always refetched — it's about now.
 */
export function cancelOptionsQueryOptions(id: string, nodeId?: string) {
  return queryOptions({
    queryKey: [...LANES_KEY, id, "cancel-options", nodeId ?? "lane"],
    queryFn: async () => {
      const res = await axiosInstance.get<CancelOptions>(
        API_ROUTES.LANES.CANCEL_OPTIONS(id),
        { params: nodeId ? { node_id: nodeId } : undefined }
      )
      return res.data
    },
    staleTime: 0,
    // Offering "delete now" alone beats retrying a dialog into a spinner.
    retry: false,
  })
}

export function useCancelOptionsQuery(
  id: string,
  nodeId: string | undefined,
  enabled: boolean
) {
  return useQuery({ ...cancelOptionsQueryOptions(id, nodeId), enabled })
}

/**
 * Cancel the whole lane — now, or at the end of the paid month. 202 with
 * `{ detail }`, which callers show as the confirmation.
 */
export function useDeleteLaneMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, when }: { id: string; when: CancelWhen }) => {
      const res = await axiosInstance.delete<{ detail: string }>(
        API_ROUTES.LANES.DELETE(id),
        { params: { when } }
      )
      return res.data
    },
    onSuccess: (_data, { id, when }) => {
      qc.invalidateQueries({ queryKey: [...LANES_KEY, "overview"] })
      qc.invalidateQueries({ queryKey: [...LANES_KEY, "list"] })
      // Deleted now: deliberately NOT the lane's own detail queries, whose
      // refetch would 404 into a "couldn't load" state — the detail page
      // navigates to the list instead. Scheduled for later: the lane is
      // still here, so refetch it to show the new `cancel_at`.
      if (when === "period_end") {
        qc.invalidateQueries({ queryKey: laneQueryOptions(id).queryKey })
      }
      qc.invalidateQueries({ queryKey: ["billing"] })
    },
  })
}

/** Undo a scheduled lane cancellation. Returns the updated lane. */
export function useKeepLaneMutation(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const res = await axiosInstance.post<LaneRead>(API_ROUTES.LANES.KEEP(id))
      return res.data
    },
    onSuccess: cacheLane(qc),
  })
}

/**
 * Scale up: add media servers in bulk, each with its own region + plan.
 * All-or-nothing server-side. 202 → the full lane (new nodes included).
 */
export function useAddMediaServersMutation(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (servers: { region: string; plan_id: string }[]) => {
      const res = await axiosInstance.post<LaneRead>(
        API_ROUTES.LANES.SERVERS(id),
        { servers }
      )
      return res.data
    },
    onSuccess: cacheLane(qc),
  })
}

/**
 * Cancel one media server — now (graceful drain + remove; the node enters
 * `draining`) or at the end of the paid month (it keeps running, with
 * `cancel_at` set). Returns the updated lane, so we re-render from it.
 */
export function useRemoveMediaServerMutation(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      nodeId,
      when,
    }: {
      nodeId: string
      when: CancelWhen
    }) => {
      const res = await axiosInstance.delete<LaneRead>(
        API_ROUTES.LANES.SERVER(id, nodeId),
        { params: { when } }
      )
      return res.data
    },
    onSuccess: cacheLane(qc),
  })
}

/** Undo a scheduled server cancellation. Returns the updated lane. */
export function useKeepMediaServerMutation(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (nodeId: string) => {
      const res = await axiosInstance.post<LaneRead>(
        API_ROUTES.LANES.SERVER_KEEP(id, nodeId)
      )
      return res.data
    },
    onSuccess: cacheLane(qc),
  })
}

/** Stop a media server — keeps billing but drops its calls. Media only. */
export function useStopMediaServerMutation(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (nodeId: string) => {
      const res = await axiosInstance.post<LaneRead>(
        API_ROUTES.LANES.SERVER_STOP(id, nodeId)
      )
      return res.data
    },
    onSuccess: cacheLane(qc),
  })
}

/** Start a stopped media server. Media only. */
export function useStartMediaServerMutation(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (nodeId: string) => {
      const res = await axiosInstance.post<LaneRead>(
        API_ROUTES.LANES.SERVER_START(id, nodeId)
      )
      return res.data
    },
    onSuccess: cacheLane(qc),
  })
}

/**
 * The IP-reissue fee for one server and whether the balance covers it
 * (`waived` when no fee applies). Keyed under billing's reissue-fee key so
 * billing refreshes reach it. Fee and balance shift — always refetched.
 */
export function reissueQuoteQueryOptions(nodeId: string) {
  return queryOptions({
    queryKey: ["billing", "reissue-fee", nodeId],
    queryFn: async () => {
      const res = await axiosInstance.get<ReissueQuote>(
        API_ROUTES.BILLING.REISSUE_FEE,
        { params: { node_id: nodeId } }
      )
      return res.data
    },
    staleTime: 0,
  })
}

export function useReissueQuoteQuery(nodeId: string, enabled: boolean) {
  return useQuery({ ...reissueQuoteQueryOptions(nodeId), enabled })
}

/**
 * Issue a fresh public IP for a media server — charged, and refunded if
 * the rebuild fails. Media only. The returned lane marks the server
 * `reissue_pending` until it is back.
 */
export function useReissueMediaIpMutation(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (nodeId: string) => {
      const res = await axiosInstance.post<LaneRead>(
        API_ROUTES.LANES.SERVER_REISSUE_IP(id, nodeId)
      )
      return res.data
    },
    onSuccess: cacheLane(qc),
  })
}
